import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Map } from 'immutable';
import { handleActions } from 'redux-actions';
import { merger } from '../../utils/helpers/model.helper';
import { IRequestError } from '../../utils/helpers/redux.helper';
import { I2gMoney } from './i2g-money.data';
import {
  ActionsEnum,
  FetchI2gMoneyContextSuccess,
  FetchI2gMoneyWebviewSuccess,
  FetchSettingsError,
  IFetchPayPalOnboardingUrlSuccess,
  SetSettingsDispatchedResponse,
  SetUnderwritingUrl,
  UpdateEditingSettings,
  UpdateI2gMoneyContextSuccess,
  FetchI2gMoneyAccountSummarySuccess,
  FetchI2gMoneyStatementsSuccess,
  UpdateSettings,
} from './settings-data.actions';
import { Settings } from './settings.model';

// feature name
export const FEATURE_NAME = 'settings-data';

export interface ISettingsDispatchedResponse extends IRequestError {
  entity: any;
}

// state definition
export interface IState {
  settings: Settings;
  editingSettings: Settings;
  originalEditingSettings: Settings;
  fetchError: Error;
  underwritingUrl: string;
  paypalOnboardingUrl: string;
  dispatchedResponse: ISettingsDispatchedResponse;
  i2gMoney: I2gMoney;
  /**
   * This is used for the spinner logo
   * in buttons for linking external
   * bank account in I2g Money
   */
  processing: boolean;
}

// initial state
export const initialState: IState = {
  settings: void 0,
  editingSettings: null,
  originalEditingSettings: null,
  fetchError: null,
  underwritingUrl: null,
  paypalOnboardingUrl: null,
  dispatchedResponse: null,
  i2gMoney: null,
  processing: false,
};

// reducer
export const buildSettings = (
  currentSettings: Settings,
  settingsProps: Map<string, any>
) => {
  return new Settings(
    merger(
      currentSettings,
      settingsProps instanceof Settings
        ? settingsProps
        : { [Settings.KEYS.CONTENT]: settingsProps }
    )
  );
};

const reducerFn = handleActions(
  {
    [ActionsEnum.FETCH_SETTINGS_SUCCESS]: function (state: IState): IState {
      return {
        ...state,
        fetchError: null,
      };
    },
    [ActionsEnum.FETCH_SETTINGS_ERROR]: function (
      state: IState,
      { payload }: FetchSettingsError
    ): IState {
      return {
        ...state,
        fetchError: payload.error,
        settings: state.settings || null,
      };
    },
    [ActionsEnum.UPDATE_SETTINGS]: function (
      state: IState,
      { payload }: UpdateSettings
    ): IState {
      const { resetOriginalEditingSettings, settingsProps } = payload;
      const newSettings: Settings = buildSettings(
        state.settings,
        settingsProps
      );
      const originalEditingSettings = resetOriginalEditingSettings
        ? newSettings
        : state.originalEditingSettings;

      return {
        ...state,
        originalEditingSettings,
        settings: newSettings,
      };
    },
    [ActionsEnum.RESET_EDITING_SETTINGS]: function (state: IState): IState {
      return {
        ...state,
        editingSettings: null,
        originalEditingSettings: null,
      };
    },
    [ActionsEnum.UPDATE_EDITING_SETTINGS]: function (
      state: IState,
      { payload }: UpdateEditingSettings
    ): IState {
      const { settingsProps } = payload;
      const prevEditingSettings = state.editingSettings;
      const currentEditingSettings =
        settingsProps === null
          ? null
          : buildSettings(
              state.editingSettings || state.settings,
              settingsProps
            );
      const originalEditingSettings =
        prevEditingSettings === null
          ? currentEditingSettings
          : state.originalEditingSettings;

      return {
        ...state,
        editingSettings: currentEditingSettings,
        originalEditingSettings,
      };
    },
    [ActionsEnum.SET_SETTINGS_DISPATCHED_RESPONSE]: function (
      state: IState,
      { payload }: SetSettingsDispatchedResponse
    ): IState {
      const { dispatchId, entity, error } = payload;
      const dispatchedResponse: ISettingsDispatchedResponse = {
        dispatchId,
        entity,
        error,
      };

      return {
        ...state,
        dispatchedResponse,
      };
    },
    [ActionsEnum.FETCH_STRIPE_ONBOARDING_URL]: function (
      state: IState
    ): IState {
      return {
        ...state,
        underwritingUrl: null,
      };
    },
    [ActionsEnum.SET_UNDERWRITING_URL]: function (
      state: IState,
      { payload }: SetUnderwritingUrl
    ): IState {
      const { underwritingUrl } = payload;
      return {
        ...state,
        underwritingUrl,
      };
    },
    [ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL]: function (
      state: IState
    ): IState {
      return {
        ...state,
        paypalOnboardingUrl: null,
      };
    },
    [ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL_SUCCESS]: function (
      state: IState,
      { payload }: IFetchPayPalOnboardingUrlSuccess
    ): IState {
      const { paypalOnboardingUrl } = payload;
      return {
        ...state,
        paypalOnboardingUrl,
      };
    },
    [ActionsEnum.FETCH_I2G_MONEY_WEBVIEW_SUCCESS]: function (
      state: IState,
      { payload }: FetchI2gMoneyWebviewSuccess
    ): IState {
      return {
        ...state,
        i2gMoney: {
          ...state.i2gMoney,
          webview: payload,
        },
      };
    },
    [ActionsEnum.FETCH_I2G_MONEY_CONTEXT_SUCCESS]: function (
      state: IState,
      { payload }: FetchI2gMoneyContextSuccess
    ): IState {
      return {
        ...state,
        i2gMoney: {
          ...state.i2gMoney,
          context: payload,
        },
      };
    },
    [ActionsEnum.UPDATE_I2G_MONEY_CONTEXT_SUCCESS]: function (
      state: IState,
      { payload }: UpdateI2gMoneyContextSuccess
    ): IState {
      return {
        ...state,
        i2gMoney: {
          ...state.i2gMoney,
          context: {
            ...state.i2gMoney.context,
            settings: payload,
          },
        },
      };
    },
    [ActionsEnum.START_LINKING_EXTERNAL_BANK_ACCOUNT]: function (
      state: IState
    ) {
      return {
        ...state,
        processing: true,
      };
    },
    [ActionsEnum.FINISH_LINKING_EXTERNAL_BANK_ACCOUNT]: function (
      state: IState
    ) {
      console.log('finished processing');
      return {
        ...state,
        processing: false,
      };
    },
    [ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY_SUCCESS]: function (
      state: IState,
      { payload }: FetchI2gMoneyAccountSummarySuccess
    ) {
      return {
        ...state,
        i2gMoney: {
          ...state.i2gMoney,
          accountSummary: payload,
        },
      };
    },
    [ActionsEnum.FETCH_I2G_MONEY_STATEMENTS_SUCCESS]: function (
      state: IState,
      { payload }: FetchI2gMoneyStatementsSuccess
    ) {
      return {
        ...state,
        i2gMoney: {
          ...state.i2gMoney,
          statements: payload,
        },
      };
    },
  } as any,
  initialState
);

export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getSettings = createSelector(
  selectFeature,
  (state: IState) => state.settings
);
export const getEditingSettings = createSelector(
  selectFeature,
  (state: IState) => state.editingSettings
);
export const getOriginalEditingSettings = createSelector(
  selectFeature,
  (state: IState) => state.originalEditingSettings
);
export const getUnderwritingUrl = createSelector(
  selectFeature,
  (state: IState) => state.underwritingUrl
);
export const getPayPalOnboardingUrl = createSelector(
  selectFeature,
  (state: IState) => state.paypalOnboardingUrl
);
export const getSettingsDispatchedResponse = createSelector(
  selectFeature,
  (state: IState) => state.dispatchedResponse
);

/** I2G Money */
export const getI2gMoney = createSelector(
  selectFeature,
  (state: IState) => state.i2gMoney
);

export const getProcessing = createSelector(
  selectFeature,
  (state: IState) => state.processing
);
